import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import Brand from '../../../components/brand';
import Button from '../../../components/primitives/button';
import ChooseLanguage from '../../../components/choose-language';
import { PictureByGender } from '../../../domains/user/ui/components/PictureByGender';
import { NotificationCounter } from '../../../domains/notification/ui/components/NotificationCounter';
import { usePortal } from '../../infrastructure/portal/usePortal';
import { SearchBarAutocomplete } from '../../components/SearchBar/SearchBarAutocomplete';
import type { HeaderViewProps } from './Header.types';

const HeaderView = ({
  className,
  navigationItems,
  showSignIn,
  showLanguageSelector,
  showNotifications,
  showProfile,
  signInLabel,
  onSignIn,
  profilePicture,
  profileGender,
  isMenuOpen,
  isNotificationsOpen,
  onProfileMenuToggle,
  onNotificationsToggle,
  notificationsCount,
  menuItems,
  menuRef,
  triggerRef,
  onHeaderSearch,
  onHeaderSuggestions,
  onHeaderSuggestionSelect,
}: HeaderViewProps) => {
  const portalRoot = usePortal('dropdown');
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useLayoutEffect(() => {
    if (!isMenuOpen || !triggerRef.current) {
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isMenuOpen, triggerRef]);

  const profileMenu =
    isMenuOpen &&
    portalRoot &&
    createPortal(
      <div
        ref={menuRef}
        className="menu-authorized bg-white border br-16 p-8"
        style={{ top: menuPosition.top, right: menuPosition.right }}
      >
        <ul className="nav__list d-flex flex-column">
          {menuItems.map((item) => (
            <li className="d-flex nav__auth-menu" key={item.id}>
              {item.href ? (
                <a
                  href={item.href}
                  onClick={item.onClick}
                  className="nav__link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={item.iconSrc} className={item.iconClass} alt="" />
                  {item.title}
                </a>
              ) : (
                <NavLink
                  to={item.route || '/'}
                  onClick={item.onClick}
                  className="nav__link"
                >
                  <img src={item.iconSrc} className={item.iconClass} alt="" />
                  {item.title}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </div>,
      portalRoot,
    );

  return (
    <header className={`app-header container-fluid ${className || ''}`.trim()}>
      <div className="container panel">
        <Brand />
        <nav className="nav">
          <div className="nav__menu" id="nav-menu">
            <ul className="nav__list d-flex">
              {navigationItems.map((item) => (
                <li key={item.id} className="nav__item">
                  {item.href ? (
                    <a
                      href={item.href}
                      className="nav__link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <NavLink to={item.route || '/'} className="nav__link">
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}

              {showSignIn && (
                <li>
                  <Button
                    onClick={onSignIn}
                    className="btn btn-secondary"
                    title={signInLabel}
                  />
                </li>
              )}

              {showProfile && onHeaderSearch && onHeaderSuggestions ? (
                <li className="nav__item nav__item--search">
                  <SearchBarAutocomplete
                    placeholder="header.search.placeholder"
                    width={250}
                    height={36}
                    isClearable
                    onSearch={onHeaderSearch}
                    onSuggestionsFetch={onHeaderSuggestions}
                    onSuggestionSelect={onHeaderSuggestionSelect}
                    minCharsForSuggestions={2}
                    maxSuggestions={5}
                    debounceDelay={300}
                  />
                </li>
              ) : null}

              {showLanguageSelector && (
                <li className={showProfile || showNotifications ? 'nav__item' : undefined}>
                  <ChooseLanguage />
                </li>
              )}

              {showNotifications && (
                <li className="nav__item">
                  <div className="cursor-pointer position-relative">
                    <NotificationCounter
                      count={notificationsCount}
                      onButtonClick={onNotificationsToggle}
                      showContent={isNotificationsOpen}
                    />
                  </div>
                </li>
              )}

              {showProfile && (
                <li className="nav__item">
                  <div
                    ref={triggerRef}
                    className="cursor-pointer position-relative"
                    onClick={onProfileMenuToggle}
                  >
                    <PictureByGender
                      clazz="w-45px"
                      picture={profilePicture}
                      gender={profileGender}
                    />
                  </div>
                  {profileMenu}
                </li>
              )}
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default HeaderView;
